/* AI ID / Iqama scan (useAiExtract feature "idscan"): source → extracted fields (tick/edit) → apply to the employee form. */
import { useId, useState } from "react";
import { useAiExtract, useUploadFile } from "../../../data/hooks";
import type { AiExtractResponse } from "../../../data/types";
import { useI18n } from "../../../i18n";
import { Alert, Button, Chip, Icon, Input, Modal, errMsg } from "../../../ui";

export interface IdScanFields { nameEn?: string; nameAr?: string; idNumber?: string; nationality?: string; dob?: string; expiry?: string }
const KEYS: (keyof IdScanFields)[] = ["nameEn", "nameAr", "idNumber", "nationality", "dob", "expiry"];
const LABEL: Record<keyof IdScanFields, [string, string]> = {
  nameEn: ["Full name (EN)", "الاسم الكامل (إنجليزي)"], nameAr: ["Full name (AR)", "الاسم الكامل (عربي)"], idNumber: ["ID / Iqama number", "رقم الهوية / الإقامة"],
  nationality: ["Nationality", "الجنسية"], dob: ["Date of birth", "تاريخ الميلاد"], expiry: ["ID / Iqama expiry", "انتهاء الهوية / الإقامة"],
};

export default function IdScanModal({ onClose, onApply }: { onClose: () => void; onApply: (f: IdScanFields) => void }) {
  const { L, t } = useI18n();
  const extract = useAiExtract();
  const upload = useUploadFile();
  const [file, setFile] = useState<File | null>(null);
  const [res, setRes] = useState<AiExtractResponse | null>(null);
  const [vals, setVals] = useState<IdScanFields>({});
  const [use, setUse] = useState<Record<string, boolean>>({});
  const [err, setErr] = useState<string | null>(null);
  const fileId = useId();
  const busy = extract.isPending || upload.isPending;

  const run = async (sample?: string) => {
    setErr(null);
    try {
      let fileKey: string | undefined;
      if (!sample && file) fileKey = (await upload.mutateAsync({ file, name: file.name, folder: "documents" })).key;
      const r = await extract.mutateAsync({ feature: "idscan", fileKey, sample });
      const v: IdScanFields = {};
      for (const k of KEYS) { const x = r.fields[k]; if (x != null && x !== "") v[k] = String(x); }
      setVals(v);
      setUse(Object.fromEntries(Object.keys(v).map((k) => [k, true])));
      setRes(r);
    } catch (e) { setErr(errMsg(e)); }
  };

  const apply = () => {
    const out: IdScanFields = {};
    for (const k of KEYS) if (use[k] && vals[k] != null) out[k] = vals[k];
    onApply(out);
    onClose();
  };

  const title = <><span className="aitag"><Icon name="sparkle" className="s" />AI</span> {L("ID & passport scan", "قراءة الهوية والجواز")}</>;
  const conf = res ? Math.round(res.confidence * 100) : 0;

  return (
    <Modal title={title} onClose={onClose} className="wide" testId="idscan-modal"
      foot={res
        ? <><Button variant="ghost" onClick={() => setRes(null)} data-testid="idscan-reset">{L("Start over", "البدء من جديد")}</Button><Button variant="pri" icon="check" onClick={apply} data-testid="idscan-apply">{L("Apply to form", "تطبيق على النموذج")}</Button></>
        : <><Button variant="ghost" onClick={onClose}>{t("Cancel")}</Button><Button variant="pri" icon="sparkle" loading={busy} disabled={!file} onClick={() => run()} data-testid="idscan-extract">{L("Extract", "استخراج")}</Button></>}>
      {!res ? (
        <div className="stack">
          <p className="muted sm" style={{ margin: 0 }}>{L("Reads Iqama, national ID, passport and family cards and fills names (EN/AR), numbers, nationality, birth and expiry dates.", "يقرأ الإقامة والهوية والجواز وسجل الأسرة ويعبئ الأسماء والأرقام والجنسية والتواريخ.")}</p>
          <label className="drop" htmlFor={fileId} style={{ cursor: "pointer", padding: 22 }}>
            <Icon name="upload" className="l" /><b>{L("Upload a file", "رفع ملف")}</b>
            <span className="sm">{file ? file.name : L("Photo, scan or PDF", "صورة أو مسح أو PDF")}</span>
            <input type="file" id={fileId} hidden accept=".pdf,.png,.jpg,.jpeg,.webp,.heic" onChange={(e) => setFile(e.target.files?.[0] ?? null)} data-testid="idscan-file" />
          </label>
          <Button variant="ghost sm" icon="eye" onClick={() => run("default")} disabled={busy} data-testid="idscan-sample">{L("Try with a sample: Iqama (front & back).jpg", "جرّب بمثال: إقامة (الوجهان).jpg")}</Button>
          {err && <Alert tone="bad" title={L("Couldn't read the document", "تعذّرت قراءة المستند")} desc={err} testId="idscan-error" />}
          <div className="aiinfo"><Icon name="shield" className="s" /><span>{L("Personal data stays on approved providers only · 1 credit · nothing is saved until you apply", "البيانات الشخصية تُرسل للمزودين المعتمدين فقط · رصيد واحد · لا يُحفظ شيء قبل التطبيق")}</span></div>
        </div>
      ) : (
        <div className="stack" data-testid="idscan-review">
          <Alert tone="ok" tint title={L(Object.keys(vals).length + " fields extracted", "تم استخراج " + Object.keys(vals).length + " حقول")} desc={L("Untick anything you don't want. Low-confidence values are highlighted — please check them.", "ألغِ تحديد ما لا تريده. القيم منخفضة الدقة مظللة، يرجى مراجعتها.")} />
          <div className="tw">
            <table className="tbl compact">
              <thead><tr><th style={{ width: 36 }} /><th>{L("Field", "الحقل")}</th><th>{L("Value", "القيمة")}</th><th className="end">{L("Confidence", "الدقة")}</th></tr></thead>
              <tbody>
                {KEYS.filter((k) => vals[k] != null).map((k) => (
                  <tr key={k} className={conf < 90 ? "lowconf" : ""}>
                    <td><input type="checkbox" checked={!!use[k]} onChange={(e) => setUse({ ...use, [k]: e.target.checked })} aria-label={L("Use ", "استخدام ") + L(LABEL[k][0], LABEL[k][1])} data-testid={"idscan-use-" + k} /></td>
                    <td className="sm">{L(LABEL[k][0], LABEL[k][1])}</td>
                    <td><Input style={{ minHeight: 34 }} value={vals[k] ?? ""} dir={k === "nameAr" ? "rtl" : undefined} aria-label={L(LABEL[k][0], LABEL[k][1])} onChange={(e) => setVals({ ...vals, [k]: e.target.value })} data-testid={"idscan-val-" + k} /></td>
                    <td className="end"><Chip tone={conf >= 95 ? "ok" : conf >= 90 ? "info" : "warn"} nodot>{conf}%</Chip></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="aiinfo"><Icon name="clock" className="s" /><span>{L("Saves about 6 minutes of typing · ", "يوفر نحو 6 دقائق من الإدخال · ")}<span className="num">{res.model}</span></span></div>
        </div>
      )}
    </Modal>
  );
}
